import React, { Component } from 'react';
import { Pagination } from 'react-bootstrap';
import _ from 'lodash';
import { underscored } from 'underscore.string';
import { withTranslation } from 'react-i18next';
import Spinner from '../../ui/LoadingCircle';
import Container from '../../v2/container';
import AddButton from '../../v2/buttons/AddButton';
import CargomaticModal from '../../v2/modal';
import CargomaticSelect from '../../v2/select';
import AccessorialCard from './AccessorialCard';
import AccessorialForm from './AccessorialForm';
import SessionStore from '../../../stores/SessionStore';
import {
  getAccessorials,
  deleteAccessorial,
} from '../../../utils/AccessorialWebAPIUtils';
import ConfirmModal from '../../ui/modals/ConfirmModal';
import ToastStore from '../../../stores/ToastStore';
import Search from '../../v2/search';
import { sluggify } from '../../../utils/localization/utils';

const shipmentTypes = [
  {
    localizationSlug: 'DRAYAGE',
    value: 'drayage',
  },
  {
    localizationSlug: 'SHORTHAUL',
    value: 'shorthaul',
  },
];

class AccessorialManagementCenter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      accessorials: [],
      showAccessorialModal: false,
      showConfirmDelete: false,
      selectedAccessorial: null,
      filters: {
        shipment_type: '',
        name: '',
        page: 1,
        per_page: 40,
        paginate: true,
      },
      pages: 1,
      canCreate: false,
      canEdit: false,
      canDelete: false,
    };

    this.fetchAccessorials = _.debounce(this.fetchAccessorials, 500);
  }

  componentDidMount() {
    const { t } = this.props;
    this.fetchAccessorials();
    SessionStore.addChangeListener(this.handleSessionStoreChange);
    this.setAccessLevel();
    document.title = t('CARGOMATIC_INC_ACCESSORIALS');
  }

  componentWillUnmount() {
    SessionStore.removeChangeListener(this.handleSessionStoreChange);
  }

  fetchAccessorials = async () => {
    this.setState({ loading: true }, async () => {
      try {
        const result = await getAccessorials(this.getFilters());
        const {
          entity: { items, pages },
        } = result;

        const accessorials = items.map((a) => ({
          ...a,
          slug: a.slug || underscored(a.name.toLowerCase()),
        }));

        this.setState({
          accessorials,
          loading: false,
          pages,
        });
      } catch (e) {
        console.error(e);
        this.setState({ loading: false });
      }
    });
  };

  getFilters() {
    const { filters } = this.state;

    return Object.keys(filters).reduce((runningMap, key) => {
      if (filters[key]) {
        return {
          ...runningMap,
          [key]: filters[key],
        };
      }
      return { ...runningMap };
    }, {});
  }

  getConfirmDeleteTitle() {
    const { t } = this.props;
    if (!this.state.showConfirmDelete) {
      return '';
    }

    return t('DELETE_ACCESSORIAL', {
      deleteAccessorial: this.state.selectedAccessorial.name,
    });
  }

  getModalTitle() {
    const { t } = this.props;
    const { selectedAccessorial } = this.state;

    if (selectedAccessorial) {
      return t('EDIT_ACCESSORIAL');
    }

    return t('ADD_ACCESSORIAL', { count: 1 });
  }

  setAccessLevel() {
    const canAccess =
      SessionStore.isGodAdmin() || SessionStore.hasAccessorialManagerRole();
    this.setState({
      canDelete: canAccess,
      canCreate: canAccess,
      canEdit: canAccess,
    });
  }

  handleSessionStoreChange = () => {
    this.setAccessLevel();
  };

  handleSelectPage = (eventKey) => {
    this.setState(
      (prevState) => {
        return {
          filters: {
            ...prevState.filters,
            page: eventKey,
          },
        };
      },
      () => this.fetchAccessorials(),
    );
  };

  handleSelectShipmentType = (item) => {
    const { value: shipment_type } = item;
    this.setState(
      (prevState) => {
        return {
          filters: {
            ...prevState.filters,
            shipment_type,
            page: 1,
          },
        };
      },
      () => {
        this.fetchAccessorials();
      },
    );
  };

  handleSearch = (name) => {
    this.setState(
      (prevState) => {
        return {
          filters: {
            ...prevState.filters,
            name,
            page: 1,
          },
        };
      },
      () => {
        this.fetchAccessorials();
      },
    );
  };

  handleConfirmDelete = async () => {
    const { t } = this.props;
    try {
      await deleteAccessorial(this.state.selectedAccessorial);
      this.setState((prevState) => {
        const { accessorials: currentAccessorials, selectedAccessorial } =
          prevState;
        const index = currentAccessorials.findIndex(
          (a) => a._id === selectedAccessorial._id,
        );
        return {
          accessorials: [
            ...currentAccessorials.slice(0, index),
            ...currentAccessorials.slice(index + 1),
          ],
          selectedAccessorial: null,
          showConfirmDelete: false,
        };
      });
      ToastStore.addNotification({
        level: 'success',
        title: t('ACCESSORIAL_DELETED_SUCCESSFULLY'),
      });
    } catch (e) {
      console.error(e);
      ToastStore.addNotification({
        level: 'error',
        title: t('PROBLEM_DELETE_ACCESSORIAL'),
      });
    }
  };

  handleDeleteAccessorial = (id) => () => {
    this.setState((prevState) => {
      const accessorial = prevState.accessorials.find((a) => a._id === id);
      if (!accessorial) {
        return null;
      }

      return {
        showConfirmDelete: true,
        selectedAccessorial: accessorial,
      };
    });
  };

  handleHideConfirmModal = () => {
    this.setState({ showConfirmDelete: false, selectedAccessorial: null });
  };

  handleAddAccessorial = () => {
    this.setState({ showAccessorialModal: true });
  };

  handleEditAccessorial = (id) => () => {
    this.setState((prevState) => {
      if (!prevState.canEdit) {
        return null;
      }
      const accessorial = prevState.accessorials.find((a) => a._id === id);

      return {
        showAccessorialModal: true,
        selectedAccessorial: accessorial,
      };
    });
  };

  handleSaveAccessorialSuccess = () => {
    this.handleHideAccessorialModal();
    this.fetchAccessorials();
  };

  handleHideAccessorialModal = () => {
    this.setState({ showAccessorialModal: false, selectedAccessorial: null });
  };

  renderAccessorials() {
    const { t } = this.props;
    if (this.state.accessorials.length === 0) {
      return <p>{t('NO_ACCESSORIALS')}</p>;
    }

    return (
      <div className="flex flex-wrap -mx-4">
        {this.state.accessorials.map((accessorial) => (
          <AccessorialCard
            key={accessorial._id}
            name={t(sluggify(accessorial.slug))}
            iconSlug={accessorial.icon_slug}
            className="px-4 mb-4"
            slug={accessorial.slug}
            canSelect={this.state.canEdit}
            canDelete={this.state.canDelete}
            onSelect={this.handleEditAccessorial(accessorial._id)}
            onDelete={this.handleDeleteAccessorial(accessorial._id)}
          />
        ))}
      </div>
    );
  }

  render() {
    const { t } = this.props;

    return (
      <Container>
        <div className="min-h-screen -mx-4 mt-10">
          <div className="flex items-center px-4 mb-4">
            <h3 className="my-0 mr-2 text-lg font-medium">
              {t('ACCESSORIAL_MANAGEMENT_CENTER')}
            </h3>
            {this.state.canCreate && (
              <AddButton onClick={this.handleAddAccessorial} />
            )}
          </div>

          <div className="flex flex-wrap items-end mb-8">
            <div className="w-full px-4 md:w-1/3">
              <Search
                value={this.state.filters.name}
                onChange={this.handleSearch}
              />
            </div>
            <div className="px-4">
              <CargomaticSelect
                selected={this.state.filters.shipment_type}
                items={shipmentTypes}
                textKey="localizationSlug"
                valueKey="value"
                label={t('SHIPMENT_TYPE')}
                onSelect={this.handleSelectShipmentType}
                showCaption
                applyDefaultMargins={false}
              />
            </div>
          </div>
          <div className="px-4 mb-4">
            {this.state.loading && <Spinner text={t('LOADING')} />}
            {this.renderAccessorials()}
            {this.state.pages > 1 && (
              <Pagination
                first
                last
                ellipsis
                items={this.state.pages}
                maxButtons={10}
                activePage={this.state.filters.page}
                onSelect={this.handleSelectPage}
              />
            )}
          </div>
          {this.state.showAccessorialModal && (
            <CargomaticModal
              show={this.state.showAccessorialModal}
              title={this.getModalTitle()}
              onHide={this.handleHideAccessorialModal}
            >
              <AccessorialForm
                accessorial={this.state.selectedAccessorial}
                onCancel={this.handleHideAccessorialModal}
                onSaveSuccess={this.handleSaveAccessorialSuccess}
              />
            </CargomaticModal>
          )}
          {this.state.showConfirmDelete && (
            <ConfirmModal
              show={this.state.showConfirmDelete}
              title={this.getConfirmDeleteTitle()}
              onConfirm={this.handleConfirmDelete}
              onHide={this.handleHideConfirmModal}
              message={t('ARE_YOU_SURE')}
            />
          )}
        </div>
      </Container>
    );
  }
}

export default withTranslation()(AccessorialManagementCenter);
